import { AuthProvider, useAuth } from "./lib/AuthContext";
import * as Sentry from "@sentry/react";
import LoginPage from "./components/LoginPage";
import OnboardingForm from "./components/OnboardingForm";
import PendingApproval from "./components/PendingApproval";
import RecruiterDashboard from "./components/RecruiterDashboard";
import PaymentWall from "./components/PaymentWall";
import DemoSwitcher from "./components/DemoSwitcher";
import { DEMO_SCHOOL_ID } from "./lib/constants";
import { usePWA } from "./hooks/usePWA";

const COLORS = {
  bg: "#F8F7F4",
  accent: "#2B5CE6",
  textMuted: "#9C9889",
};

function StagingBanner() {
  if (import.meta.env.MODE !== "staging") return null;
  return (
    <div style={{
      backgroundColor: "#F59E0B",
      color: "#1A1917",
      textAlign: "center",
      padding: "4px 0",
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: "0.05em",
      position: "sticky",
      top: 0,
      zIndex: 9999,
    }}>
      STAGING ENVIRONMENT
    </div>
  );
}

function UpdateBanner() {
  const { needRefresh, updateServiceWorker, dismissRefresh } = usePWA();
  if (!needRefresh) return null;
  return (
    <div style={{
      position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 10000,
      backgroundColor: COLORS.accent, color: "#fff", padding: "12px 20px",
      display: "flex", alignItems: "center", justifyContent: "center", gap: 12,
      fontFamily: "'DM Sans', sans-serif", fontSize: 14,
    }}>
      <span>A new version is available.</span>
      <button onClick={() => updateServiceWorker(true)} style={{
        padding: "6px 16px", borderRadius: 6, border: "2px solid #fff",
        backgroundColor: "transparent", color: "#fff", fontSize: 13,
        fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
      }}>Update now</button>
      <button onClick={dismissRefresh} style={{
        padding: "6px 10px", borderRadius: 6, border: "none",
        backgroundColor: "transparent", color: "rgba(255,255,255,0.7)", fontSize: 13,
        cursor: "pointer", fontFamily: "inherit",
      }}>Dismiss</button>
    </div>
  );
}

function AppRouter() {
  const { user, profile, loading } = useAuth();

  // Loading state
  if (loading) {
    return (
      <div style={{
        fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif",
        backgroundColor: COLORS.bg, minHeight: "100vh",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
        <div style={{ textAlign: "center" }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12, backgroundColor: COLORS.accent,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            color: "#fff", fontWeight: 700, fontSize: 22, marginBottom: 16,
          }}>R</div>
          <p style={{ color: COLORS.textMuted, fontSize: 14 }}>Loading...</p>
        </div>
      </div>
    );
  }

  // Not logged in
  if (!user) {
    return <LoginPage />;
  }

  // Logged in but no profile yet — show onboarding
  if (!profile) {
    return <OnboardingForm />;
  }

  // Admin/careers users skip onboarding, approval, and payment — go straight to dashboard
  if (profile.role === "admin" || profile.role === "careers") {
    return (
      <>
        <RecruiterDashboard />
        <DemoSwitcher />
      </>
    );
  }

  // Profile exists but onboarding not completed — show onboarding
  if (!profile.onboarding_completed) {
    return <OnboardingForm />;
  }

  // Onboarding done but not approved yet
  if (profile.status !== "approved") {
    return <PendingApproval />;
  }

  // Approved recruiter — free or pro tier, both get the dashboard
  // Free tier has usage limits enforced server-side (search, connections, CV downloads)
  return (
    <>
      <RecruiterDashboard />
      <DemoSwitcher />
    </>
  );
}

function ErrorFallback({ resetError }) {
  return (
    <div style={{
      fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif",
      backgroundColor: COLORS.bg, minHeight: "100vh",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20,
    }}>
      <div style={{ textAlign: "center", maxWidth: 400 }}>
        <div style={{
          width: 48, height: 48, borderRadius: 12, backgroundColor: "#E5484D",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          color: "#fff", fontWeight: 700, fontSize: 22, marginBottom: 16,
        }}>!</div>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: "#1A1A1A", marginBottom: 8 }}>
          Something went wrong
        </h2>
        <p style={{ fontSize: 14, color: "#6B7280", marginBottom: 20 }}>
          An unexpected error occurred. Our team has been notified.
        </p>
        <button onClick={resetError} style={{
          padding: "10px 24px", borderRadius: 8, border: "none",
          backgroundColor: COLORS.accent, color: "#fff", fontSize: 14,
          fontWeight: 600, fontFamily: "inherit", cursor: "pointer",
        }}>
          Try Again
        </button>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <Sentry.ErrorBoundary fallback={({ resetError }) => <ErrorFallback resetError={resetError} />}>
      <StagingBanner />
      <AuthProvider>
        <AppRouter />
      </AuthProvider>
      <UpdateBanner />
    </Sentry.ErrorBoundary>
  );
}
